import type { Option } from "@tento-lona/sheets";
import type { UserId } from "@tento-lona/sheets";
import type { TypedApi } from "@tento-core/typed-api";
import { Uuid } from "@tento-core/uuid";
import { InstanceUrl } from "@tento-core/instance-url";
import { type DateTime, NaiveDate, Tzname, Utc } from "@tento-chrono";
import { Field, Model, Scalar } from "@tento-core/model";
import { RowWriteError, SdkRequestError } from "../../errors.ts";
import { AfterFailedWrite, afterFailedWrite } from "../../client/after-failed-write.ts";
import { Tracing } from "@tento-core/tracing";
import { Network } from "../../network.ts";
import { Ledger } from "@tento-lona/sheets/internal-sdk";
import { Actor, type InvalidationSink, SchedulingLinksChanged } from "../../invalidation.ts";
import {
  Json,
  type JsonObject,
  Patch,
  RowKey,
  RowNetworkKey,
  SchedulingLinkAttributes,
  WireDecode,
  WireDecodeError,
} from "@tento-lona/sheets";

/**
 * Placeholder range for the receipt cells read. The server looks the booking up
 * by its `booking_id` capability and ignores `start`/`end` on that path, but the
 * cells query requires the fields — a fixed date keeps the call deterministic.
 */
const BOOKING_READ_PLACEHOLDER_DATE = NaiveDate.fromYmd1(2020, 1, 1).exp();

const ROWS_ROUTE = "/api/rows";
const ROW_ROUTE = "/api/rows/{rowKey}";
const CELLS_ROUTE = "/api/rows/{rowKey}/cells";
const ROWS_LIST_ROUTE = "/api/rows/all";

/** What `schedulingLinksClientFor` builds a store over (the SDK's own door). */
export interface SchedulingLinksClientDeps {
  readonly api: TypedApi<Network.Routes>;
  /** The user whose links the store holds; baked onto every `SchedulingLinks.Row`. */
  readonly ownerId: UserId;
  /** The client's sink: a landed `create` / `update` emits `SchedulingLinksChanged`. */
  readonly invalidation: InvalidationSink;
  /** The client's lifecycle check, run first by every network verb. */
  readonly assertLive: (verb: string) => void;
}

/**
 * A scheduling-links store over `deps` — the SDK's door for
 * `LonaSdk.Client.schedulingLinksFor(userId)`, which is how an app gets one.
 * Not on a barrel; the barrels export the store's type only, so its
 * constructor is reachable from the SDK alone.
 */
export function schedulingLinksClientFor(deps: SchedulingLinksClientDeps): SchedulingLinksClient {
  return new SchedulingLinksClient(deps);
}

/**
 * **A user's scheduling links** (booking pages) — the store
 * `client.schedulingLinksFor(userId)` hands out, one per user. Exported as a
 * type: an app never constructs one.
 *
 * Scheduling links are rows of type `"scheduling-link"` with lookup key
 * `:bookings:{slug}`. Link configuration lives in the row's attributes;
 * public lookup reads the projection row `:bookings:{slug}:availability`.
 * The owner is baked onto every `SchedulingLinks.Row` the store holds, so a
 * surface renders its URLs through the row (`row.bookingUrl()`).
 *
 * Every request is checked, and every network verb checks the client's
 * lifecycle first (a store held past `client.dispose()` refuses with
 * `LonaSdk.Client.DisposedError`). `ensureLoaded` throws `SdkRequestError`
 * (boundary `scheduling-links`, step `list`); `create` / `update` throw
 * `RowWriteError` (steps `add` / `update`). The held list changes only
 * after every request of a call succeeded, and a landed `create` / `update`
 * emits `SchedulingLinksChanged` stamped with its `actor`.
 */
export class SchedulingLinksClient {
  /**
   * The actor the store's own corrections are announced as: the
   * `SchedulingLinksChanged` a failed write's re-read emits
   * (`update` unanswered, or its take-back).
   */
  static readonly ACTOR = new Actor("scheduling-links");

  #links: SchedulingLinks.Row[] = [];
  #loaded = false;
  #loading: Option<Promise<void>> = null;
  readonly #ownerId: UserId;
  readonly #api: TypedApi<Network.Routes>;
  readonly #invalidation: InvalidationSink;
  readonly #assertLive: (verb: string) => void;
  /**
   * The links with updates in flight: the scheduling-link instance of the
   * one ledger (`Ledger`, a pure model — the held list shows its confirmed
   * state). An update is sent in two parts (`Ledger.sendPart`): its link
   * row, then its public projection row.
   */
  readonly #pending = new Ledger<LinkWrites>(LinkFields.STRATEGY, {
    sinks: () => [],
    name: "scheduling-links-update",
    takeBackContext: "the take-back of a refused scheduling link update",
    describe: (link) => ({ link: link.toString() }),
    live: () => AfterFailedWrite.isLive({ assertLive: (verb) => this.#assertLive(verb) }),
    abandoned: (link, fields, error) =>
      Tracing.warn("net")("scheduling-links-update/take-back-abandoned", {
        link: link.toString(),
        fields: [...fields].join(","),
        error: error.message,
      }),
  });

  /**
   * A store over `deps` (`schedulingLinksClientFor`, the SDK's own door). The
   * barrels export `SchedulingLinksClient` as a type only: an app never
   * constructs one, it asks `client.schedulingLinksFor(userId)`.
   */
  constructor(deps: SchedulingLinksClientDeps) {
    this.#api = deps.api;
    this.#ownerId = deps.ownerId;
    this.#invalidation = deps.invalidation;
    this.#assertLive = deps.assertLive;
  }

  /**
   * Load the owner's links once (`GET /api/rows/all?rowType=scheduling-link`);
   * later calls resolve at once. Concurrent calls share the one read. A failed
   * load throws `SdkRequestError` (step `list`), keeps the held list, and is
   * not remembered: the next call reads again.
   */
  async ensureLoaded(): Promise<void> {
    this.#assertLive("schedulingLinks.ensureLoaded");
    if (this.#loaded) return;
    const loading = this.#loading ??= this.#load();
    try {
      await loading;
    } finally {
      if (this.#loading === loading) this.#loading = null;
    }
  }

  async #load(): Promise<void> {
    // A read of each held link: updates that land while it is in flight
    // lay over its answer, and the updates in flight keep replaying.
    const reads = this.#links.map((l) => this.#pending.beginRead(l.id));
    let response: Network.PaginatedRowsResponse;
    try {
      response = await this.#list();
    } catch (err) {
      // after-failed-write: a failed read closes its reads unanswered — nothing was written, nothing is taken back.
      for (const read of reads) this.#pending.settleRead(read, null);
      throw err;
    }
    const links: SchedulingLinks.Row[] = [];
    for (const item of response.items) {
      const link = rowItemToRow(item, this.#ownerId);
      if (link) links.push(link);
    }
    const byId = new Map(links.map((l) => [l.id, l]));
    for (const read of reads) {
      const fresh = byId.get(read.key) ?? null;
      const reread = this.#pending.settleRead(read, fresh == null ? null : LinkFields.of(fresh));
      if (fresh == null || reread == null) continue;
      const confirmed = this.#pending.confirmed(read.key) ?? reread;
      const at = links.indexOf(fresh);
      links[at] = LinkFields.row(fresh, confirmed);
    }
    this.#links = links;
    this.#loaded = true;
  }

  /** The owner's link rows (`GET /api/rows/all?rowType=scheduling-link`). */
  #list(): Promise<Network.PaginatedRowsResponse> {
    return SdkRequestError.checkRequest(
      "scheduling-links",
      "list",
      "GET",
      ROWS_LIST_ROUTE,
      () =>
        this.#api.getResult(ROWS_LIST_ROUTE, {
          query: {
            rowType: "scheduling-link",
            deleted: null,
            page: null,
            pageSize: null,
            sortBy: null,
            sortDirection: null,
          },
        }),
    );
  }

  /** The held links, in load order (a copy); empty before `ensureLoaded`. */
  peekAll(): readonly SchedulingLinks.Row[] {
    return [...this.#links];
  }

  /** The first link, after `ensureLoaded`; unset when the owner has none. */
  async getFirstRow(): Promise<Option<SchedulingLinks.Row>> {
    this.#assertLive("schedulingLinks.getFirstRow");
    await this.ensureLoaded();
    return this.#links[0] ?? null;
  }

  /**
   * Create a link: its row (`:bookings:{slug}`), then its public projection
   * row (`:bookings:{slug}:availability`), both `POST /api/rows`. Throws
   * `RowWriteError` (step `add`) when either is refused or unanswered, and
   * nothing is added to the held list. A refused projection takes the link
   * row back (`DELETE /api/rows/{rowKey}`), so no half-made link stays on
   * the server; when that delete fails too, the error's `details` say the
   * link row stayed. A row whose POST went unanswered (or was answered
   * unreadably: `SdkRequestError.mayHaveStored`) may have been stored: it
   * is deleted too (its id is the client's), so neither a link row without
   * its projection nor a projection without its link stays on the server.
   * `actor` stamps the `SchedulingLinksChanged` a landed
   * create emits (`Actor.EXTERNAL` for a caller with no echo of its own).
   */
  async create(
    request: SchedulingLinks.CreateRequest,
    actor: Actor,
  ): Promise<SchedulingLinks.Row> {
    this.#assertLive("schedulingLinks.create");
    const id = Network.RowUuid.make();
    const now = Utc.now();
    const attrs: SchedulingLinks.Attributes = {
      slug: request.slug,
      description: request.description,
      ownerName: request.ownerName,
      durationMinutes: request.durationMinutes,
      timezone: request.timezone,
      availability: [...request.availability],
      isActive: true,
      createdAt: now,
      updatedAt: now,
      renderMode: null,
    };

    const owner: AfterFailedWrite.Owner = { assertLive: (verb) => this.#assertLive(verb) };
    try {
      await RowWriteError.check("add", "POST", ROWS_ROUTE, () =>
        this.#api.postResult(ROWS_ROUTE, {
          body: Network.NewRow.make({
            id,
            type: "scheduling-link",
            label: request.title,
            sheetId: null,
            lookupKey: `:bookings:${request.slug}`,
            attributes: SchedulingLinks.Attributes.toJson(attrs),
            parentId: null,
            surface: null,
          }),
        }));
    } catch (err) {
      // The link row may have stored without its projection: delete it.
      afterFailedWrite(err, owner, "schedulingLinks.create", () => this.#deleteRow(id));
      throw err;
    }
    const projectionId = Network.RowUuid.make();
    try {
      await RowWriteError.check("add", "POST", ROWS_ROUTE, () =>
        this.#api.postResult(ROWS_ROUTE, {
          body: Network.NewRow.make({
            id: projectionId,
            type: "scheduling-link",
            label: request.title,
            sheetId: null,
            lookupKey: `:bookings:${request.slug}:availability`,
            attributes: SchedulingLinks.Attributes.toJson(attrs),
            parentId: null,
            surface: null,
          }),
        }));
    } catch (err) {
      // The projection row may have stored: delete it too (the take-back
      // below deletes the link row).
      afterFailedWrite(err, owner, "schedulingLinks.create", () => this.#deleteRow(projectionId));
      throw await this.#takeBackLinkRow(id, request.slug, err);
    }

    const row = new SchedulingLinks.Row({
      id: RowNetworkKey.fromWire(id),
      ownerId: this.#ownerId,
      label: request.title,
      lookupKey: `:bookings:${request.slug}`,
      attributes: attrs,
    });
    this.#links.push(row);
    this.#invalidation.emit(new SchedulingLinksChanged(actor, this.#ownerId));
    return row;
  }

  /**
   * Delete row `id` (`DELETE /api/rows/{rowKey}`), a row a failed create may
   * have stored — a failed create's recovery (`afterFailedWrite`, which
   * traces a delete that fails). A row the server does not hold is gone
   * already.
   */
  async #deleteRow(id: Uuid): Promise<void> {
    await RowWriteError.check("delete", "DELETE", ROW_ROUTE, () =>
      SdkRequestError.notFoundAsNull(() =>
        this.#api.delResult(ROW_ROUTE, { path: { rowKey: RowNetworkKey.fromWire(id) } })
      ));
  }

  /**
   * The compensating delete of a create whose projection row `err` refused:
   * delete the link row `id` it had created, and answer the `RowWriteError`
   * (step `add`) the create rejects with — saying whether the link row was
   * taken back or stayed on the server.
   */
  async #takeBackLinkRow(id: Uuid, slug: string, err: unknown): Promise<RowWriteError> {
    const what = `scheduling link ${slug}: its public projection row was not created`;
    try {
      await RowWriteError.check("delete", "DELETE", ROW_ROUTE, () =>
        this.#api.delResult(ROW_ROUTE, { path: { rowKey: RowNetworkKey.fromWire(id) } }));
    } catch (undo) {
      const words = undo instanceof Error ? undo.message : String(undo);
      return RowWriteError.after(
        err,
        "add",
        "POST",
        ROWS_ROUTE,
        `${what}, and deleting its link row failed too (${words}), so the link row stays on the server`,
      );
    }
    return RowWriteError.after(err, "add", "POST", ROWS_ROUTE, `${what}; its link row was deleted again`);
  }

  /**
   * Edit the held link `link` (a `SchedulingLinks.Row` this store handed
   * out: `peekAll`, `create`, an earlier `update`): its row, then its public
   * projection row, both `PATCH /api/rows/{rowKey}`. `patch` is the SDK's
   * one patch vocabulary (`SchedulingLinks.Patch`): a field it names is set
   * — or, for the `description`, cleared with `Patch.clear` — and a field it
   * omits is left as it is. Resolves with the link as this update left it on
   * the server once both landed. Throws `RowWriteError` (step `update`) when
   * either is refused or unanswered, or (no status, nothing sent) when the
   * store holds no link of `link.id`.
   *
   * Overlapping updates of one link share one ledger (`Ledger`, as row
   * attributes do): the server replaces the whole
   * attribute bag, so every PATCH body is the link's confirmed state with
   * EVERY update in flight laid over it in issue order — a later update
   * never erases an earlier one's field. The held list shows the confirmed
   * state: an update folds into it once it and every older update of the
   * link landed. A rejection leaves nothing edited that the store does not
   * show: the refused update's own fields go back to what the surviving
   * updates say, and when a row may carry them (its own projection patch
   * failed after the link row's landed, or a later update's body carried
   * them) that row is PATCHed with the surviving state; the error's
   * `details` say whether the link row was restored or stayed edited. When a
   * patch may have stored (`SdkRequestError.mayHaveStored`) — or a take-back
   * failed — the links are re-read and announced (`SchedulingLinksChanged`
   * as `SchedulingLinksClient.ACTOR`). `actor` stamps the
   * `SchedulingLinksChanged` a landed update emits.
   */
  async update(
    link: SchedulingLinks.Row,
    patch: SchedulingLinks.Patch,
    actor: Actor,
  ): Promise<SchedulingLinks.Row> {
    this.#assertLive("schedulingLinks.update");
    const id = link.id;
    const existing = this.#links.find((l) => l.id === id);
    if (!existing) {
      throw new RowWriteError({
        step: "update",
        method: "PATCH",
        path: ROW_ROUTE,
        failure: { outcome: "unaddressable" },
        details: `scheduling link ${id} is not held`,
      });
    }
    const owner: AfterFailedWrite.Owner = { assertLive: (verb) => this.#assertLive(verb) };

    const edit: LinkFields = {
      ...(patch.title !== undefined ? { label: patch.title } : {}),
      ...(patch.description !== undefined ? { description: Patch.wire(patch.description) } : {}),
      ...(patch.durationMinutes !== undefined ? { durationMinutes: patch.durationMinutes } : {}),
      ...(patch.timezone !== undefined ? { timezone: patch.timezone } : {}),
      ...(patch.availability !== undefined ? { availability: [...patch.availability] } : {}),
      ...(patch.isActive !== undefined ? { isActive: patch.isActive } : {}),
      updatedAt: Utc.now(),
    };
    const write = this.#pending.begin(id, edit, actor, {
      correction: SchedulingLinksClient.ACTOR,
      quiet: false,
      capture: () => LinkFields.of(this.#heldOf(id) ?? existing),
    });
    const projectionKey = RowNetworkKey.fromWire(`:bookings:${existing.slug}:availability`);
    const what = `scheduling link ${existing.slug}: its public projection row was not updated`;
    // The state this update's projection PATCH carried (what it resolves with).
    let sent: LinkFields = edit;
    try {
      await this.#pending.sendPart(write, LinkFields.LINK_ROW, this.#rowTransport(id, null), () => false);
    } catch (err) {
      // Refused: its own fields go back to the surviving writes. A later
      // write's body carried them: the ledger PATCHes the surviving state over them.
      const settled = this.#settle(write, { accepted: false, error: err, context: "schedulingLinks.update" });
      void settled.takenBack.then((failure) => {
        if (failure != null && AfterFailedWrite.isLive(owner)) void this.#reloadQuietly();
      });
      // No verdict (or one the SDK could not read): the link row may be
      // edited — re-read the links.
      afterFailedWrite(err, owner, "schedulingLinks.update", () => this.#reload());
      throw err;
    }
    try {
      await this.#pending.sendPart(
        write,
        LinkFields.PROJECTION_ROW,
        this.#rowTransport(projectionKey, (state) => {
          sent = state;
        }),
        (err) => SdkRequestError.mayHaveStored(err),
      );
    } catch (err) {
      // The link row landed: the ledger takes it back to the surviving state
      // (and the projection, when its patch may have stored or a later
      // write's body carried this one); re-read when anything may disagree.
      const settled = this.#settle(write, { accepted: false, error: err, context: "schedulingLinks.update" });
      const failure = await settled.takenBack;
      if (failure != null) {
        // The take-back failed: the link row may stay edited whatever `err`
        // was, so the links are re-read.
        if (AfterFailedWrite.isLive(owner)) void this.#reloadQuietly();
        throw RowWriteError.after(
          err,
          "update",
          "PATCH",
          ROW_ROUTE,
          `${what}, and restoring its link row failed too (${failure.message}), so the link row may stay edited on the server`,
        );
      }
      afterFailedWrite(err, owner, "schedulingLinks.update", () => this.#reload());
      throw RowWriteError.after(err, "update", "PATCH", ROW_ROUTE, `${what}; its link row was restored`);
    }

    this.#settle(write, Ledger.ACCEPTED);
    this.#invalidation.emit(new SchedulingLinksChanged(actor, this.#ownerId));
    return LinkFields.row(existing, sent);
  }

  /** The held link of `id`, if any. */
  #heldOf(id: RowNetworkKey): Option<SchedulingLinks.Row> {
    return this.#links.find((l) => l.id === id) ?? null;
  }

  /**
   * One PATCH of `rowKey` (the link row or its projection) as a ledger
   * transport: the body is the state the ledger cut for it (every
   * attribute — the server replaces the whole bag — and the label when the
   * write, or the take-back, carries it). `onFirst` sees the first state
   * sent through it (the update's own).
   */
  #rowTransport(rowKey: RowNetworkKey, onFirst: Option<(state: LinkFields) => void>): Ledger.Transport<LinkWrites> {
    let first = onFirst;
    return (state) => {
      first?.(state);
      first = null;
      return RowWriteError.check("update", "PATCH", ROW_ROUTE, () =>
        this.#api.patchResult(ROW_ROUTE, { path: { rowKey }, body: LinkFields.body(state, state.label !== undefined) }));
    };
  }

  /** Settle `write` on its `verdict` and move the held link to the
   *  confirmed state the settle leaves (`Ledger.settle`). */
  #settle(write: Ledger.Write<LinkWrites>, verdict: Ledger.Verdict): Ledger.Settled<LinkWrites> {
    const settled = this.#pending.settle(write, verdict);
    const at = this.#links.findIndex((l) => l.id === write.key);
    const held = this.#links[at];
    if (held != null && settled.confirmed != null) this.#links[at] = LinkFields.row(held, settled.confirmed);
    return settled;
  }

  /**
   * A failed `update`'s recovery (`afterFailedWrite`): re-read the owner's
   * links (`GET /api/rows/all?rowType=scheduling-link`), replace the held
   * list with them and announce it (`SchedulingLinksChanged` as
   * `SchedulingLinksClient.ACTOR`). The re-read is authoritative: it
   * becomes each link's confirmed state, with the updates that landed
   * during it laid over (`Ledger.settleRead`).
   */
  async #reload(): Promise<void> {
    await this.#load();
    this.#invalidation.emit(new SchedulingLinksChanged(SchedulingLinksClient.ACTOR, this.#ownerId));
  }

  /** `#reload`, a failure traced (the re-read after a failed take-back). */
  #reloadQuietly(): Promise<void> {
    return this.#reload().catch((e: unknown) =>
      Tracing.warn("net")(`schedulingLinks.update: the re-read after a failed take-back failed: ${String(e)}`)
    );
  }

  /**
   * The public booking page's link — `address` names it (`{ username, slug }`,
   * both parsed: `SchedulingLinks.UserName.parse`, `SchedulingLinks.Slug.parse`)
   * — read from its public projection row (`GET /api/rows/{rowKey}`,
   * anonymous). `null` when there is no such row (a 404) or the link is not
   * an active one. Throws `SdkRequestError` (boundary `scheduling-links`,
   * step `get`) when the read is refused (any status but 404) or unanswered.
   */
  async getBookingLink(
    address: SchedulingLinks.BookingAddress,
  ): Promise<Option<SchedulingLinks.BookingLink>> {
    this.#assertLive("schedulingLinks.getBookingLink");
    const rowKey = SchedulingLinks.bookingAvailabilityRowKey(address.username, address.slug);
    const response = await SdkRequestError.checkRequest(
      "scheduling-links",
      "get",
      "GET",
      ROW_ROUTE,
      () =>
        SdkRequestError.notFoundAsNull(() =>
          this.#api.getResult(ROW_ROUTE, {
            path: { rowKey: RowNetworkKey.forRead(RowNetworkKey.fromWire(rowKey)) },
          })
        ),
    );
    if (response == null) return null;
    const bag = Json.object(response.attributes);
    if (bag == null) return null;
    const attrs = SchedulingLinks.Attributes.decode(bag);
    if (attrs == null || !attrs.isActive) return null;
    return Object.freeze({
      ownerId: address.username,
      ownerName: attrs.ownerName,
      durationSec: attrs.durationMinutes * 60,
      title: response.label ?? attrs.slug,
      summary: attrs.description ?? "",
      slug: attrs.slug,
      timezone: attrs.timezone,
      description: attrs.description,
    });
  }

  /**
   * Read a confirmed booking back for the public receipt page (anonymous).
   *
   * The booking *is* a cell on the link's row, looked up by its UUID — the
   * unguessable capability — via the cells endpoint's `booking_id` filter (the
   * same row key the page writes the booking through). The receipt is composed
   * client-side from that booking cell plus the link metadata
   * (`getBookingLink(address)`), so no synthetic row or assembled-DTO
   * endpoint is needed. Returns `null` when the link is missing or inactive,
   * or the id doesn't match a booking. Throws `SdkRequestError` (boundary
   * `scheduling-links`, step `get-cells`) when the cells read is refused or
   * unanswered, and with outcome `"malformed"` when the booking cell does not
   * decode as a `Booking` (`SchedulingLinks.Booking.parse`; a malformed
   * booking is not a missing one). A caller holding the id as text parses it
   * first (`Uuid.parse`).
   */
  async getBooking(
    address: SchedulingLinks.BookingAddress,
    bookingId: Uuid,
  ): Promise<Option<SchedulingLinks.BookingReceipt>> {
    this.#assertLive("schedulingLinks.getBooking");
    const rowKey = SchedulingLinks.bookingAvailabilityRowKey(address.username, address.slug);
    const [link, cells] = await Promise.all([
      this.getBookingLink(address),
      SdkRequestError.checkRequest(
        "scheduling-links",
        "get-cells",
        "GET",
        CELLS_ROUTE,
        () =>
          this.#api.getResult(CELLS_ROUTE, {
            path: { rowKey: RowNetworkKey.forRead(RowNetworkKey.fromWire(rowKey)) },
            query: {
              start: BOOKING_READ_PLACEHOLDER_DATE,
              end: BOOKING_READ_PLACEHOLDER_DATE,
              mock_sync_delay: null,
              booking_id: bookingId,
            },
          }),
      ),
    ]);
    if (!link) return null;
    const cell = cells[0] ?? null;
    if (cell == null) return null;
    let booking: SchedulingLinks.Booking;
    try {
      booking = SchedulingLinks.Booking.parse(cell.data);
    } catch (err) {
      if (!(err instanceof WireDecodeError)) throw err;
      const malformed = new SdkRequestError({
        boundary: "scheduling-links",
        step: "get-cells",
        method: "GET",
        path: CELLS_ROUTE,
        failure: { outcome: "malformed" },
        details: `booking ${bookingId}: ${err.message}`,
      });
      malformed.cause = err;
      throw malformed;
    }
    return {
      hostName: address.username,
      linkTitle: link.title,
      durationMinutes: Math.round(link.durationSec / 60),
      timezone: link.timezone,
      startTime: booking.startTime,
      endTime: booking.endTime,
      bookerName: booking.bookerName,
      bookerEmail: booking.bookerEmail,
      status: booking.status,
    };
  }
}

/**
 * **The scheduling-link vocabulary** — a link as the store holds it (`Row`),
 * the create / update requests, the public booking reads' answers, and the
 * booking URLs. Values an app builds and reads; the store itself is
 * `client.schedulingLinksFor(userId)` (`SchedulingLinksClient`).
 */
export namespace SchedulingLinks {
  /**
   * A lona username as a booking URL names it (`/booking/{username}/{slug}`) —
   * not a `UserId`. Parse it at the edge (`UserName.parse(routeParam)`).
   */
  export type UserName = string & { readonly __brand: "SchedulingLinks.UserName" };

  export namespace UserName {
    /** Whether `raw` can name a user in a booking URL and row key: non-empty, no `/`, `:`, `[`, `]` or whitespace. */
    export function is(raw: string): raw is UserName {
      return raw.length > 0 && !/[/:\[\]\s]/.test(raw);
    }
    /** `raw` as a `UserName`, or `null` when it cannot be one (`is`). */
    export function parse(raw: string): Option<UserName> {
      return is(raw) ? raw : null;
    }
  }

  /** A scheduling link's slug (`/booking/{username}/{slug}`, lookup key `:bookings:{slug}`). */
  export type Slug = string & { readonly __brand: "SchedulingLinks.Slug" };

  export namespace Slug {
    /** Whether `raw` can be a link's slug: non-empty, no `/`, `:`, `[`, `]` or whitespace. */
    export function is(raw: string): raw is Slug {
      return raw.length > 0 && !/[/:\[\]\s]/.test(raw);
    }
    /** `raw` as a `Slug`, or `null` when it cannot be one (`is`). */
    export function parse(raw: string): Option<Slug> {
      return is(raw) ? raw : null;
    }
  }

  /** Which public booking link a read names: its owner's username and its slug. */
  export interface BookingAddress {
    readonly username: UserName;
    readonly slug: Slug;
  }

  export function bookingPath(username: string, slug: string): string {
    return `/booking/${encodeURIComponent(username)}/${encodeURIComponent(slug)}`;
  }

  export function bookingUrl(username: string, slug: string): string {
    return InstanceUrl.calendar(bookingPath(username, slug));
  }

  export function bookingAvailabilityRowKey(username: string, slug: string): string {
    return `[${username}]:bookings:${slug}:availability`;
  }

  export class Row {
    /**
     * The link row's key as the server takes it (`PATCH /api/rows/{rowKey}`):
     * the id `create` minted, or the one the list read.
     */
    readonly id: RowNetworkKey;
    readonly ownerId: UserId;
    readonly label: Option<string>;
    readonly lookupKey: Option<string>;
    /** The link's configuration, decoded and frozen (`SchedulingLinks.Attributes`). */
    readonly attributes: Attributes;

    constructor(fields: {
      id: RowNetworkKey;
      ownerId: UserId;
      label: Option<string>;
      lookupKey: Option<string>;
      attributes: Attributes;
    }) {
      this.id = fields.id;
      this.ownerId = fields.ownerId;
      this.label = fields.label;
      this.lookupKey = fields.lookupKey;
      this.attributes = Attributes.frozen(fields.attributes);
    }

    get slug(): Slug {
      return this.attributes.slug;
    }

    get title(): string {
      return this.label ?? this.attributes.slug;
    }

    get durationMinutes(): number {
      return this.attributes.durationMinutes;
    }

    bookingPath(): string {
      return bookingPath(this.ownerId, this.slug);
    }

    bookingUrl(): string {
      return bookingUrl(this.ownerId, this.slug);
    }

    /**
     * The link as its public booking page shows it. Throws when the owner's
     * id cannot name a user in a booking URL (`UserName.is`) — the page this
     * link would point at does not exist.
     */
    toBookingLink(): BookingLink {
      const ownerId = UserName.parse(this.ownerId);
      if (ownerId == null) {
        throw new Error(`SchedulingLinks.Row.toBookingLink: owner ${this.ownerId} cannot name a booking URL`);
      }
      return Object.freeze({
        ownerId,
        ownerName: this.attributes.ownerName,
        durationSec: this.durationMinutes * 60,
        title: this.title,
        summary: this.attributes.description ?? "",
        slug: this.slug,
        timezone: this.attributes.timezone,
        description: this.attributes.description,
      });
    }
  }

  /**
   * A link's configuration as a `Row` hands it out: its attribute bag decoded,
   * every field stated (`null` for one the link does not have), the slug a
   * `Slug`, the zone a `Tzname`, the stamps UTC instants. A `Row` freezes it
   * (and each availability entry).
   */
  export interface Attributes {
    readonly slug: Slug;
    readonly description: Option<string>;
    /** Display name of the link owner, shown on the public booking page. */
    readonly ownerName: Option<string>;
    readonly durationMinutes: number;
    readonly timezone: Tzname;
    readonly availability: readonly Readonly<SchedulingLinkAttributes.WeeklyAvailability>[];
    readonly isActive: boolean;
    readonly createdAt: DateTime<Utc>;
    readonly updatedAt: DateTime<Utc>;
    /** How the row renders in the calendar (`null`: the default, `"cells"`). */
    readonly renderMode: Option<SchedulingLinkAttributes.RenderMode>;
  }

  export namespace Attributes {
    /**
     * A link row's stored attribute bag as `Attributes`: `null` unless it
     * decodes (`SchedulingLinkAttributes.decode`: a non-empty `slug`) and
     * carries every required field, the slug is one (`Slug.is`), the zone
     * is an IANA name the runtime knows (`Tzname.parseOpt`) and both stamps
     * parse as instants — a bag that fails is not a link this client can
     * show or book.
     */
    export function decode(bag: JsonObject): Option<Attributes> {
      const a = SchedulingLinkAttributes.decode(bag);
      if (a == null) return null;
      const slug = Slug.parse(a.slug);
      const timezone = a.timezone == null ? null : Tzname.parseOpt(a.timezone);
      const createdAt = Utc.parse(a.createdAt);
      const updatedAt = Utc.parse(a.updatedAt);
      if (
        slug == null || a.durationMinutes == null || timezone == null ||
        a.isActive == null || createdAt == null || updatedAt == null
      ) return null;
      return frozen({
        slug,
        description: a.description,
        ownerName: a.ownerName,
        durationMinutes: a.durationMinutes,
        timezone,
        availability: a.availability ?? [],
        isActive: a.isActive,
        createdAt,
        updatedAt,
        renderMode: a.renderMode,
      });
    }

    /**
     * `attrs` as the row attribute bag the server stores (`decode`'s
     * inverse): the known fields, an absent (`null`) `description` /
     * `ownerName` / `renderMode` left out, the stamps as RFC 3339 instants.
     */
    export function toJson(attrs: Attributes): JsonObject {
      const bag: { [key: string]: Json } = {
        slug: attrs.slug,
        durationMinutes: attrs.durationMinutes,
        timezone: attrs.timezone,
        availability: SchedulingLinkAttributes.availabilityToJson(attrs.availability),
        isActive: attrs.isActive,
        createdAt: attrs.createdAt.toString(),
        updatedAt: attrs.updatedAt.toString(),
      };
      if (attrs.description != null) bag.description = attrs.description;
      if (attrs.ownerName != null) bag.ownerName = attrs.ownerName;
      if (attrs.renderMode != null) bag.renderMode = attrs.renderMode;
      return bag;
    }

    /** `attributes` frozen, each availability entry too (a `Row`'s own copy). */
    export function frozen(attributes: Attributes): Attributes {
      return Object.freeze({
        ...attributes,
        availability: Object.freeze(attributes.availability.map((entry) => Object.freeze({ ...entry }))),
      });
    }
  }

  /** A new link, as `create` takes it: every field stated (`null` for one it does not have). */
  export interface CreateRequest {
    readonly slug: Slug;
    readonly title: string;
    readonly description: Option<string>;
    /** Owner's display name, shown on the public booking page. */
    readonly ownerName: Option<string>;
    readonly durationMinutes: number;
    readonly timezone: Tzname;
    readonly availability: readonly SchedulingLinkAttributes.WeeklyAvailability[];
  }

  /**
   * What `update` writes, in the SDK's one patch vocabulary (`Patch`, as
   * `RowPatch` and `TasksClient.Patch` take it): a field the patch names is
   * set to its value — the `description` can instead be cleared with
   * `Patch.clear` — and a field it omits is left as it is.
   *
   * ```ts
   * await links.update(link.id, { isActive: false, description: Patch.clear }, actor);
   * ```
   */
  export type Patch = {
    readonly title?: string;
    readonly description?: Patch.Clearable<string>;
    readonly durationMinutes?: number;
    readonly timezone?: Tzname;
    readonly availability?: readonly SchedulingLinkAttributes.WeeklyAvailability[];
    readonly isActive?: boolean;
  };

  /**
   * A public booking link as the booking page shows it (`getBookingLink`,
   * `Row.toBookingLink`), frozen. It is addressed by its owner's username and
   * its slug (`/booking/{ownerId}/{slug}`).
   */
  export interface BookingLink {
    /** The owner's username, as the booking URL names it. */
    readonly ownerId: UserName;
    /** Owner's display name, when the link has one. */
    readonly ownerName: Option<string>;
    readonly durationSec: number;
    readonly title: string;
    readonly summary: string;
    readonly slug: Slug;
    readonly timezone: Tzname;
    readonly description: Option<string>;
  }

  /**
   * The booking cell's `data` payload, as returned by the public cells read
   * (`GET /api/rows/{availabilityKey}/cells?booking_id=…`). Matches the server
   * `Booking` DTO (camelCase), decoded: ids are `Uuid`s, times UTC instants.
   */
  export interface Booking {
    id: Uuid;
    schedulingLinkId: Uuid;
    startTime: DateTime<Utc>;
    endTime: DateTime<Utc>;
    bookerName: string;
    bookerEmail: string;
    status: BookingStatus;
    createdAt: DateTime<Utc>;
  }

  /** A booking's state, as the server's `BookingStatus` spells it. */
  export type BookingStatus = "pending" | "confirmed" | "cancelled" | "completed";

  export namespace Booking {
    /**
     * The booking cell's `data` as a `Booking`: every field required, ids and
     * times checked. Throws `WireDecodeError` (`type` `"SchedulingLinks.Booking"`)
     * naming what did not decode.
     */
    export function parse(raw: Json): Booking {
      return WireDecode.parse("SchedulingLinks.Booking", MODEL, raw);
    }

    /** The booking cell's decoder: every field required, ids and times checked. */
    export const MODEL: Model.Object<Booking> = Model.object<Booking>({
      id: Field.required(Scalar.checked<string, Uuid>("Uuid", Scalar.string, Uuid.parse, null)),
      schedulingLinkId: Field.required(Scalar.checked<string, Uuid>("Uuid", Scalar.string, Uuid.parse, null)),
      startTime: Field.required(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      endTime: Field.required(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      bookerName: Field.required(Scalar.string),
      bookerEmail: Field.required(Scalar.string),
      status: Field.required(Scalar.stringUnion(["pending", "confirmed", "cancelled", "completed"] as const)),
      createdAt: Field.required(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
    });
  }

  /**
   * Confirmed-booking receipt for the public `/confirmed` page, composed
   * client-side from the booking cell + the link metadata. Times are UTC
   * instants.
   */
  export interface BookingReceipt {
    hostName: UserName;
    linkTitle: string;
    durationMinutes: number;
    timezone: Tzname;
    startTime: DateTime<Utc>;
    endTime: DateTime<Utc>;
    bookerName: string;
    bookerEmail: string;
    status: BookingStatus;
  }
}

/**
 * A link's editable state as its pending-write model holds it: the row's
 * label and every attribute, each field replaced whole (the server replaces
 * the attribute bag). A patch names only the fields a write touches.
 */
type LinkFields = { -readonly [K in keyof LinkBag]?: LinkBag[K] };
interface LinkBag extends SchedulingLinks.Attributes {
  readonly label: Option<string>;
}

const LINK_FIELDS: readonly (keyof LinkBag)[] = [
  "label", "slug", "description", "ownerName", "durationMinutes", "timezone",
  "availability", "isActive", "createdAt", "updatedAt", "renderMode",
];

namespace LinkFields {
  /** `row`'s whole state. */
  export function of(row: SchedulingLinks.Row): LinkFields {
    return { ...row.attributes, label: row.label };
  }

  /** `held` with `fields` laid over (its id, owner and lookup key kept). */
  export function row(held: SchedulingLinks.Row, fields: LinkFields): SchedulingLinks.Row {
    const { label, ...attributes } = fields;
    return new SchedulingLinks.Row({
      id: held.id,
      ownerId: held.ownerId,
      label: label !== undefined ? label : held.label,
      lookupKey: held.lookupKey,
      attributes: { ...held.attributes, ...attributes },
    });
  }

  /** The PATCH body of a whole state `state` (every attribute; the label
   *  when `labelled`). */
  export function body(
    state: LinkFields,
    labelled: boolean,
  ): { readonly label: Option<string>; readonly attributes: JsonObject } | { readonly attributes: JsonObject } {
    const { label, ...attributes } = state;
    const bag = SchedulingLinks.Attributes.toJson(whole(attributes));
    return labelled && label !== undefined ? { label, attributes: bag } : { attributes: bag };
  }

  /** A whole attribute state as `Attributes` (a model's base is captured
   *  from a held link, so every field is present). */
  function whole(a: LinkFields): SchedulingLinks.Attributes {
    const missing = LINK_FIELDS.filter((k) => k !== "label" && a[k] === undefined);
    if (a.slug === undefined || a.description === undefined || a.ownerName === undefined ||
      a.durationMinutes === undefined || a.timezone === undefined || a.availability === undefined ||
      a.isActive === undefined || a.createdAt === undefined || a.updatedAt === undefined || a.renderMode === undefined) {
      throw new Error(`schedulingLinks: a link state lacks ${missing.join(", ")}`);
    }
    return {
      slug: a.slug,
      description: a.description,
      ownerName: a.ownerName,
      durationMinutes: a.durationMinutes,
      timezone: a.timezone,
      availability: a.availability,
      isActive: a.isActive,
      createdAt: a.createdAt,
      updatedAt: a.updatedAt,
      renderMode: a.renderMode,
    };
  }

  function copy<K extends keyof LinkBag>(to: LinkFields, from: LinkFields, key: K): void {
    const value = from[key];
    if (value !== undefined) to[key] = value;
  }

  /** The fields of the link row's PATCH (`Ledger.sendPart`). */
  export const LINK_ROW: ReadonlySet<LinkField> = new Set<LinkField>(["link.bag", "link.label"]);
  /** The fields of the public projection row's PATCH (`Ledger.sendPart`). */
  export const PROJECTION_ROW: ReadonlySet<LinkField> = new Set<LinkField>(["projection.bag", "projection.label"]);

  /** Copy every attribute (`bag`) or the label of `from` onto `to` (a field `from` lacks stays as it is). */
  function copyPart(to: LinkFields, from: LinkFields, field: LinkField): void {
    if (field === "link.label" || field === "projection.label") {
      copy(to, from, "label");
      return;
    }
    for (const key of LINK_FIELDS) if (key !== "label") copy(to, from, key);
  }

  /**
   * The ledger strategy of a link: a patch lays over a state field by field;
   * the ledger's fields are the two rows' bags and labels (the server
   * replaces a row's whole attribute bag, so every update writes both bags,
   * and a label only when it names one); a body is the whole bag (and the
   * label) of one row. A pure model: the store shows its confirmed state.
   */
  export const STRATEGY: Ledger.Strategy<LinkWrites> = {
    empty: () => ({}),
    merge: (target, patch) => ({ ...target, ...patch }),
    fieldsOf: (patch) => {
      const fields = new Set<LinkField>(["link.bag", "projection.bag"]);
      if (patch.label !== undefined) {
        fields.add("link.label");
        fields.add("projection.label");
      }
      return fields;
    },
    body: (state, only) => {
      const out: LinkFields = {};
      for (const field of only) copyPart(out, state, field);
      return out;
    },
    adopt: (state, from, only) => {
      const out: LinkFields = { ...state };
      for (const field of only) copyPart(out, from, field);
      return out;
    },
    overlay: (under, over) => ({ ...under, ...over }),
    takeBack: () => ({}),
    delta: () => null,
    events: () => Ledger.SILENT,
    deferred: () => {
      throw new Error("scheduling-links-update: the link ledger defers no announcement");
    },
    wrap: (err) => (err instanceof Error ? err : new Error(String(err))),
  };
}

/** A field of the link ledger: one row's (the link's, its projection's) attribute bag or label. */
type LinkField = "link.bag" | "link.label" | "projection.bag" | "projection.label";

/** The type bundle of the scheduling-link instance of `Ledger` (keyed by the link row). */
interface LinkWrites extends Ledger.Types {
  readonly key: RowNetworkKey;
  readonly change: LinkFields;
  readonly fields: LinkFields;
  readonly field: LinkField;
  readonly delta: LinkFields;
  readonly answer: unknown;
  readonly error: Error;
}

function rowItemToRow(
  item: Network.AllRowItem,
  ownerId: UserId,
): Option<SchedulingLinks.Row> {
  const bag = Json.object(item.attributes);
  if (bag == null) return null;
  const attrs = SchedulingLinks.Attributes.decode(bag);
  if (attrs == null) return null;
  if (item.lookupKey?.endsWith(":availability")) return null;
  return new SchedulingLinks.Row({
    id: RowNetworkKey.of(item.id),
    ownerId,
    label: item.label,
    lookupKey: item.lookupKey,
    attributes: attrs,
  });
}
